"use client";

import { c as _c } from "react/compiler-runtime";
import { IconBook } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconArrowRotateAnticlockwiseOutline18, IconCheckOutline18 } from "nucleo-ui-outline-18";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import FormField from "@/app/(v2)/workflows/_components/form-fields/FormField";
import { JsonSchemaProperty } from "./types";
interface X402InputSectionProps {
  integration: {
    payloadSchema?: {
      properties?: Record<string, JsonSchemaProperty>;
      required?: string[];
    };
  };
  previewValues?: Record<string, unknown>;
  setPreviewValues?: (values: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void;
  formKey?: number;
  setFormKey?: (fn: (prev: number) => number) => void;
  searchQuery: string;
  /** When true, renders a static parameter list instead of interactive form fields. */
  readOnly?: boolean;
}

/**
 * X402InputSection - Specialized input section for x402 payment-gated endpoints.
 *
 * Differences from generic input section:
 * - Hides endpointId (it's baked into the action type)
 * - Expands nested queryParams/body objects into individual fields
 * - Groups params by type (query vs body)
 */
export function X402InputSection(t0) {
  const $ = _c(42);
  const {
    integration,
    previewValues: t1,
    setPreviewValues: t2,
    formKey: t3,
    setFormKey: t4,
    searchQuery,
    readOnly: t5
  } = t0;
  let t6;
  if ($[0] !== t1) {
    t6 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const previewValues = t6;
  const setPreviewValues = t2 === undefined ? _temp : t2;
  const formKey = t3 === undefined ? 0 : t3;
  const setFormKey = t4 === undefined ? _temp2 : t4;
  const readOnly = t5 === undefined ? false : t5;
  let t7;
  if ($[2] !== integration.payloadSchema?.properties) {
    t7 = integration.payloadSchema?.properties || {};
    $[2] = integration.payloadSchema?.properties;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const properties = t7;
  let t8;
  if ($[4] !== properties.queryParams?.properties) {
    t8 = properties.queryParams?.properties || {};
    $[4] = properties.queryParams?.properties;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const queryParamsSchema = t8;
  let t9;
  if ($[6] !== properties.queryParams?.required) {
    t9 = properties.queryParams?.required || [];
    $[6] = properties.queryParams?.required;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const queryParamsRequired = t9;
  let t10;
  if ($[8] !== properties.body?.properties) {
    t10 = properties.body?.properties || {};
    $[8] = properties.body?.properties;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const bodySchema = t10;
  let t11;
  if ($[10] !== properties.body?.required) {
    t11 = properties.body?.required || [];
    $[10] = properties.body?.required;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const bodyRequired = t11;
  let t12;
  let t13;
  if ($[12] !== bodyRequired || $[13] !== bodySchema || $[14] !== formKey || $[15] !== previewValues || $[16] !== queryParamsRequired || $[17] !== queryParamsSchema || $[18] !== readOnly || $[19] !== searchQuery || $[20] !== setFormKey || $[21] !== setPreviewValues) {
    t13 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const allParams = [];
      Object.entries(queryParamsSchema).forEach(t14 => {
        const [name, schema] = t14;
        if (!schema.hidden) {
          allParams.push({
            name,
            schema,
            isRequired: queryParamsRequired.includes(name),
            group: "query",
            parentKey: "queryParams"
          });
        }
      });
      Object.entries(bodySchema).forEach(t15 => {
        const [name_0, schema_0] = t15;
        if (!schema_0.hidden) {
          allParams.push({
            name: name_0,
            schema: schema_0,
            isRequired: bodyRequired.includes(name_0),
            group: "body",
            parentKey: "body"
          });
        }
      });
      const filteredParams = searchQuery.trim() ? allParams.filter(param => param.name.toLowerCase().includes(searchQuery.toLowerCase()) || param.schema.description?.toLowerCase().includes(searchQuery.toLowerCase())) : allParams;
      const totalFields = allParams.length;
      const requiredCount = allParams.filter(_temp3).length;
      const handleValueChange = (parentKey, fieldName, value) => {
        setPreviewValues(prev => ({
          ...prev,
          [parentKey]: {
            ...(prev[parentKey] as Record<string, unknown> || {}),
            [fieldName]: value
          }
        }));
      };
      const hasValues = Object.keys(previewValues).some(key => {
        const val = previewValues[key];
        return val && typeof val === "object" && Object.keys(val).length > 0;
      });
      if (readOnly) {
        let t16;
        let t17;
        if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
          t16 = <IconBook className="text-ds-text-brand h-5 w-5" />;
          t17 = <h2 className="text-ds-text-primary text-xl font-semibold">Input Parameters</h2>;
          $[24] = t16;
          $[25] = t17;
        } else {
          t16 = $[24];
          t17 = $[25];
        }
        const t18 = totalFields > 0 && <span className="text-ds-text-muted text-sm">({totalFields} field{totalFields !== 1 ? "s" : ""}{requiredCount > 0 && `, ${requiredCount} required`})</span>;
        let t19;
        if ($[26] !== t18) {
          t19 = <div className="mb-4 flex items-center gap-2">{t16}{t17}{t18}</div>;
          $[26] = t18;
          $[27] = t19;
        } else {
          t19 = $[27];
        }
        const t20 = totalFields === 0 ? <p className="body-sm">This endpoint has no configurable parameters.</p> : filteredParams.length === 0 ? <p className="body-sm">No fields match your search query.</p> : <Accordion type="multiple" defaultValue={filteredParams.map(_temp4)}>{filteredParams.map(_temp6)}</Accordion>;
        let t21;
        if ($[28] !== t19 || $[29] !== t20) {
          t21 = <div>{t19}{t20}</div>;
          $[28] = t19;
          $[29] = t20;
          $[30] = t21;
        } else {
          t21 = $[30];
        }
        t13 = t21;
        break bb0;
      }
      let t16;
      let t17;
      if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = <IconBook className="text-ds-text-brand h-5 w-5" />;
        t17 = <h2 className="text-ds-text-primary text-xl font-semibold">Input Parameters</h2>;
        $[31] = t16;
        $[32] = t17;
      } else {
        t16 = $[31];
        t17 = $[32];
      }
      const t18 = totalFields > 0 && <span className="text-ds-text-muted text-sm">({totalFields} field{totalFields !== 1 ? "s" : ""}{requiredCount > 0 && `, ${requiredCount} required`})</span>;
      let t19;
      if ($[33] !== t18) {
        t19 = <div className="flex items-center gap-2">{t16}{t17}{t18}</div>;
        $[33] = t18;
        $[34] = t19;
      } else {
        t19 = $[34];
      }
      let t20;
      if ($[35] !== hasValues || $[36] !== setFormKey || $[37] !== setPreviewValues) {
        t20 = hasValues && <Button variant="link-brand" className="text-ds-text-disabled dark:text-ds-text-subtle h-auto" onClick={() => {
          setPreviewValues({});
          setFormKey(_temp7);
        }}><div className="flex items-center gap-2"><IconArrowRotateAnticlockwiseOutline18 className="h-3.5 w-3.5" />Clear settings</div></Button>;
        $[35] = hasValues;
        $[36] = setFormKey;
        $[37] = setPreviewValues;
        $[38] = t20;
      } else {
        t20 = $[38];
      }
      let t21;
      if ($[39] !== t19 || $[40] !== t20) {
        t21 = <div className="mb-4 flex items-center justify-between gap-2">{t19}{t20}</div>;
        $[39] = t19;
        $[40] = t20;
        $[41] = t21;
      } else {
        t21 = $[41];
      }
      t12 = <div>{t21}{totalFields === 0 ? <p className="body-sm">This endpoint has no configurable parameters.</p> : filteredParams.length === 0 ? <p className="body-sm">No fields match your search query.</p> : <Accordion type="multiple" defaultValue={filteredParams.map(_temp8)}>{filteredParams.map(param_1 => {
            const fieldKey_0 = `${param_1.parentKey}.${param_1.name}`;
            const parentValues = previewValues[param_1.parentKey] as Record<string, unknown> | undefined;
            const currentValue = parentValues?.[param_1.name];
            return <AccordionItem key={fieldKey_0} value={fieldKey_0} className="border-ds-border-default border-b"><AccordionTrigger className="py-4 hover:no-underline"><div className="flex w-full items-center justify-between pr-4"><div className="flex items-center gap-2"><code className="text-ds-text-primary bg-ds-info-surface dark:bg-ds-neutral-700 rounded px-2 py-1 font-mono text-sm font-semibold">{param_1.name}</code>{param_1.isRequired && <Badge variant="destructive"><IconCheckOutline18 className="h-3 w-3" />Required</Badge>}<Badge>{param_1.group === "query" ? "query" : "body"}</Badge></div><Badge variant="info" className="px-2.5">{param_1.schema.type || "string"}</Badge></div></AccordionTrigger><AccordionContent className="space-y-3 pb-4">{param_1.schema.description && <p className="body-sm">{param_1.schema.description}</p>}{param_1.schema.enum && <div><div className="text-ds-text-muted mb-1 text-xs font-medium">Allowed values:</div><div className="flex flex-wrap gap-1">{param_1.schema.enum.map(_temp9)}</div></div>}{param_1.schema.default !== undefined && <div className="label-xs">Default:{" "}<code className="bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded px-1 py-0.5 font-mono">{String(param_1.schema.default)}</code></div>}<div className="py-3"><FormField key={`${fieldKey_0}-${formKey}`} name={param_1.name} property={{
                    ...param_1.schema,
                    required: param_1.isRequired
                  }} value={currentValue} onChange={(name_1, value_2) => handleValueChange(param_1.parentKey, name_1, value_2)} allValues={previewValues} currentNodeId="preview" allNodes={[]} allEdges={[]} currentNodePayload={previewValues} hideLabel={true} /></div></AccordionContent></AccordionItem>;
          })}</Accordion>}</div>;
    }
    $[12] = bodyRequired;
    $[13] = bodySchema;
    $[14] = formKey;
    $[15] = previewValues;
    $[16] = queryParamsRequired;
    $[17] = queryParamsSchema;
    $[18] = readOnly;
    $[19] = searchQuery;
    $[20] = setFormKey;
    $[21] = setPreviewValues;
    $[22] = t12;
    $[23] = t13;
  } else {
    t12 = $[22];
    t13 = $[23];
  }
  if (t13 !== Symbol.for("react.early_return_sentinel")) {
    return t13;
  }
  return t12;
}
function _temp9(value_1) {
  return <code key={String(value_1)} className="bg-ds-neutral-200 text-ds-text-secondary dark:bg-ds-neutral-700 rounded px-2 py-0.5 font-mono text-xs">{String(value_1)}</code>;
}
function _temp8(p_1) {
  return `${p_1.parentKey}.${p_1.name}`;
}
function _temp7(prev_0) {
  return prev_0 + 1;
}
function _temp6(param_0) {
  const fieldKey = `${param_0.parentKey}.${param_0.name}`;
  return <AccordionItem key={fieldKey} value={fieldKey} className="border-ds-border-default border-b"><AccordionTrigger className="py-4 hover:no-underline"><div className="flex w-full items-center justify-between pr-4"><div className="flex items-center gap-2"><code className="text-ds-text-primary bg-ds-info-surface dark:bg-ds-neutral-700 rounded px-2 py-1 font-mono text-sm font-semibold">{param_0.name}</code>{param_0.isRequired && <Badge variant="destructive"><IconCheckOutline18 className="h-3 w-3" />Required</Badge>}<Badge>{param_0.group === "query" ? "query" : "body"}</Badge></div><Badge variant="info" className="px-2.5">{param_0.schema.type || "string"}</Badge></div></AccordionTrigger><AccordionContent className="space-y-3 pb-4">{param_0.schema.description && <p className="body-sm">{param_0.schema.description}</p>}{param_0.schema.enum && <div><div className="text-ds-text-muted mb-1 text-xs font-medium">Allowed values:</div><div className="flex flex-wrap gap-1">{param_0.schema.enum.map(_temp5)}</div></div>}{param_0.schema.default !== undefined && <div className="label-xs">Default:{" "}<code className="bg-ds-neutral-200 dark:bg-ds-neutral-700 rounded px-1 py-0.5 font-mono">{String(param_0.schema.default)}</code></div>}<div className="bg-ds-neutral-50 dark:bg-ds-neutral-800/50 flex items-center justify-between rounded-md px-3 py-2"><div className="flex flex-col gap-0.5"><code className="text-ds-text-secondary font-mono text-sm">{param_0.name}</code>{param_0.schema.description && <span className="label-xs">{param_0.schema.description}</span>}</div><Badge variant="info">{param_0.schema.type || "string"}</Badge></div></AccordionContent></AccordionItem>;
}
function _temp5(value_0) {
  return <code key={String(value_0)} className="bg-ds-neutral-200 text-ds-text-secondary dark:bg-ds-neutral-700 rounded px-2 py-0.5 font-mono text-xs">{String(value_0)}</code>;
}
function _temp4(p_0) {
  return `${p_0.parentKey}.${p_0.name}`;
}
function _temp3(p) {
  return p.isRequired;
}
function _temp2() {}
function _temp() {}